import { useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { useAuth } from '../../context/AuthContext.js';
import { Button } from '../../components/ui/Button.js';
import { useT } from '../../i18n/index.js';
import { auth as authApi } from '../../lib/api.js';

const inputStyle: React.CSSProperties = {
  backgroundColor: 'var(--ac-surface)',
  borderColor: 'var(--ac-border)',
  color: 'var(--ac-text)',
};
const MAX_PASSWORD_LENGTH = 256;

export default function AccountSettingsPage() {
  const { user, logout, setUser } = useAuth();
  const [, navigate] = useLocation();
  const t = useT();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [savingName, setSavingName] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const [addingPasskey, setAddingPasskey] = useState(false);
  const [passkeyMsg, setPasskeyMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  const savingNameStarted = useRef(false);
  const changingPasswordStarted = useRef(false);
  const addingPasskeyStarted = useRef(false);
  const signingOutStarted = useRef(false);

  const onSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingNameStarted.current) return;
    savingNameStarted.current = true;
    setNameMsg(null);
    setSavingName(true);
    try {
      const trimmed = displayName.trim();
      const updated = await authApi.updateMe(trimmed.length ? trimmed : null);
      setUser(updated);
      setNameMsg({ kind: 'ok', text: 'Saved.' });
    } catch {
      setNameMsg({ kind: 'err', text: 'Could not save your display name. Please try again.' });
    } finally {
      savingNameStarted.current = false;
      setSavingName(false);
    }
  };

  const onChangePw = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg(null);
    if (newPw.length < 12) {
      setPwMsg({ kind: 'err', text: 'New password must be at least 12 characters.' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ kind: 'err', text: 'New passwords do not match.' });
      return;
    }
    if (curPw.length > MAX_PASSWORD_LENGTH || newPw.length > MAX_PASSWORD_LENGTH) {
      setPwMsg({ kind: 'err', text: 'Password is too long.' });
      return;
    }
    if (changingPasswordStarted.current) return;
    changingPasswordStarted.current = true;
    setSavingPw(true);
    try {
      await authApi.changePassword(curPw, newPw);
      setPwMsg({ kind: 'ok', text: 'Password changed.' });
      setCurPw('');
      setNewPw('');
      setConfirmPw('');
    } catch {
      setPwMsg({
        kind: 'err',
        text: 'Could not change your password. Check your current password and try again.',
      });
    } finally {
      changingPasswordStarted.current = false;
      setSavingPw(false);
    }
  };

  const onAddPasskey = async () => {
    if (!user || addingPasskeyStarted.current) return;
    addingPasskeyStarted.current = true;
    setPasskeyMsg(null);
    setAddingPasskey(true);
    try {
      const options = (await authApi.getPasskeyRegisterOptions(
        user.email,
        user.displayName ?? undefined,
      )) as unknown as PublicKeyCredentialCreationOptionsJSON;
      const credential = await startRegistration({ optionsJSON: options });
      await authApi.verifyPasskeyRegister(credential);
      setPasskeyMsg({ kind: 'ok', text: 'Passkey added. You can use it on your next sign-in.' });
    } catch (err) {
      const cancelled = err instanceof DOMException && err.name === 'NotAllowedError';
      setPasskeyMsg({
        kind: 'err',
        text: cancelled
          ? 'Passkey setup was cancelled or timed out.'
          : 'Could not add the passkey. Please try again.',
      });
    } finally {
      addingPasskeyStarted.current = false;
      setAddingPasskey(false);
    }
  };

  const onSignOut = async () => {
    if (signingOutStarted.current) return;
    signingOutStarted.current = true;
    try {
      await logout();
      navigate('/login');
    } finally {
      signingOutStarted.current = false;
    }
  };

  const label = { color: 'var(--ac-text-muted)' } as const;
  const text = { color: 'var(--ac-text)' } as const;

  return (
    <div className="max-w-3xl mx-auto p-4 flex flex-col gap-6">
      <h1 className="text-xl font-semibold" style={text}>
        {t('settings.account')}
      </h1>

      <section
        className="p-4 rounded-xl border flex flex-col gap-3"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <div className="text-sm font-medium" style={text}>
          Account
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide" style={label}>
            Email
          </div>
          <div style={text}>{user?.email ?? '—'}</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-wide" style={label}>
            Primary home
          </div>
          <div style={text}>{user?.primaryHomeId ?? '—'}</div>
        </div>
      </section>

      <section
        className="p-4 rounded-xl border flex flex-col gap-3"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <div className="text-sm font-medium" style={text}>
          Passkeys
        </div>
        <p className="text-sm" style={label}>
          Add this device for faster sign-in with Face ID, Touch ID, Windows Hello, or a security
          key.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <Button type="button" onClick={() => void onAddPasskey()} disabled={addingPasskey}>
            {addingPasskey ? 'Adding passkey…' : 'Add passkey'}
          </Button>
          {passkeyMsg ? (
            <span
              role={passkeyMsg.kind === 'err' ? 'alert' : 'status'}
              className="text-sm"
              style={{ color: passkeyMsg.kind === 'ok' ? 'var(--ac-success)' : 'var(--ac-error)' }}
            >
              {passkeyMsg.text}
            </span>
          ) : null}
        </div>
      </section>

      <form
        onSubmit={onSaveName}
        className="p-4 rounded-xl border flex flex-col gap-3"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <div className="text-sm font-medium" style={text}>
          Display name
        </div>
        <input
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          maxLength={80}
          placeholder="Your name"
          className="rounded-lg border px-3 py-2 text-sm"
          style={inputStyle}
        />
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={savingName}>
            {savingName ? 'Saving…' : 'Save'}
          </Button>
          {nameMsg && (
            <span
              className="text-sm"
              style={{ color: nameMsg.kind === 'ok' ? 'var(--ac-accent)' : '#e06161' }}
            >
              {nameMsg.text}
            </span>
          )}
        </div>
      </form>

      <form
        onSubmit={onChangePw}
        className="p-4 rounded-xl border flex flex-col gap-3"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <div className="text-sm font-medium" style={text}>
          Change password
        </div>
        <input
          type="password"
          autoComplete="current-password"
          value={curPw}
          onChange={(e) => setCurPw(e.target.value)}
          maxLength={MAX_PASSWORD_LENGTH}
          placeholder="Current password"
          className="rounded-lg border px-3 py-2 text-sm"
          style={inputStyle}
        />
        <input
          type="password"
          autoComplete="new-password"
          value={newPw}
          onChange={(e) => setNewPw(e.target.value)}
          maxLength={MAX_PASSWORD_LENGTH}
          placeholder="New password (min. 12 characters)"
          className="rounded-lg border px-3 py-2 text-sm"
          style={inputStyle}
        />
        <input
          type="password"
          autoComplete="new-password"
          value={confirmPw}
          onChange={(e) => setConfirmPw(e.target.value)}
          maxLength={MAX_PASSWORD_LENGTH}
          placeholder="Confirm new password"
          className="rounded-lg border px-3 py-2 text-sm"
          style={inputStyle}
        />
        <div className="flex items-center gap-3">
          <Button type="submit" disabled={savingPw || !curPw || !newPw}>
            {savingPw ? 'Changing…' : 'Change password'}
          </Button>
          {pwMsg && (
            <span
              className="text-sm"
              style={{ color: pwMsg.kind === 'ok' ? 'var(--ac-accent)' : '#e06161' }}
            >
              {pwMsg.text}
            </span>
          )}
        </div>
      </form>

      <a
        href="/auth/password-reset"
        className="text-xs underline self-start"
        style={{ color: 'var(--ac-text-muted)' }}
      >
        Forgot your current password? Reset via email →
      </a>

      <a
        href="/settings/homes"
        className="p-4 rounded-xl border text-sm"
        style={{
          backgroundColor: 'var(--ac-surface)',
          borderColor: 'var(--ac-border)',
          color: 'var(--ac-text)',
        }}
      >
        Manage homes &amp; bridges (delete, re-pair, invite) →
      </a>

      <Button variant="danger" onClick={onSignOut}>
        {t('settings.sign_out')}
      </Button>
    </div>
  );
}
